/**
 * Dónde cae el umbral dentro de la distribución real del vehículo.
 *
 * Es el gráfico que responde la única pregunta que importa al calibrar: «si
 * pongo la línea aquí, ¿a qué porcentaje de las maniobras estoy llamando
 * bruscas?». Un umbral en el p62 marca al 38 % — eso no es un evento notable,
 * es la conducción normal de ese carro.
 */

import { percentile } from "@core/calibration/calibration-lab.ts";

const BUCKETS = 40;

export function DistributionStrip({
  label,
  unit,
  values,
  threshold,
}: {
  label: string;
  unit: string;
  /** Valores medidos, en positivo. */
  values: number[];
  threshold: number;
}) {
  if (!values.length) {
    return (
      <div className="text-xs text-zinc-500 dark:text-zinc-400">
        {label}: no hay mediciones en este período.
      </div>
    );
  }

  const sorted = [...values].sort((a, b) => a - b);
  const max = sorted[sorted.length - 1]!;
  const top = Math.max(max, threshold) * 1.05 || 1;

  const counts = new Array<number>(BUCKETS).fill(0);
  for (const value of sorted) {
    const slot = Math.min(BUCKETS - 1, Math.floor((value / top) * BUCKETS));
    counts[slot] = (counts[slot] ?? 0) + 1;
  }
  const peak = Math.max(...counts, 1);

  // Qué porcentaje de las maniobras queda marcado con este umbral. Es la cifra
  // que se puede defender frente a un cliente, y la que decide el negocio.
  const above = sorted.filter((value) => value >= threshold).length;
  const markedPercent = Math.round((above / sorted.length) * 1000) / 10;
  // Y en qué percentil está la línea.
  const rank = Math.round(((sorted.length - above) / sorted.length) * 1000) / 10;

  const p = (q: number) => {
    const value = percentile(sorted, q);
    return value === null ? "—" : value.toLocaleString("es-CO", { maximumFractionDigits: 3 });
  };

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
          {label} · {sorted.length.toLocaleString("es-CO")} mediciones
        </span>
        <span className="text-xs text-zinc-500 dark:text-zinc-400">
          p50 {p(50)} · p90 {p(90)} · p95 {p(95)} · p99 {p(99)} {unit}
        </span>
      </div>

      <div className="relative mt-1 flex h-16 items-end gap-px">
        {counts.map((count, index) => {
          const bucketValue = ((index + 0.5) / BUCKETS) * top;
          const marked = bucketValue >= threshold;
          return (
            <div
              key={index}
              className={`flex-1 rounded-t-sm ${
                marked ? "bg-amber-500/80" : "bg-zinc-300 dark:bg-zinc-700"
              }`}
              style={{ height: `${Math.max(2, (count / peak) * 100)}%` }}
              title={`${bucketValue.toFixed(2)} ${unit} · ${count} mediciones`}
            />
          );
        })}
        <div
          className="pointer-events-none absolute inset-y-0 w-0.5 bg-brand-600"
          style={{ left: `${Math.min(100, (threshold / top) * 100)}%` }}
        />
      </div>

      <p className="mt-1 text-xs leading-snug text-zinc-600 dark:text-zinc-400">
        Con el umbral en{" "}
        <strong className="tabular-nums">
          {threshold.toLocaleString("es-CO", { maximumFractionDigits: 3 })} {unit}
        </strong>{" "}
        quedan marcadas{" "}
        <strong className="tabular-nums">{markedPercent.toLocaleString("es-CO")} %</strong> de las
        maniobras de este vehículo — la línea está en el percentil{" "}
        <strong className="tabular-nums">{rank.toLocaleString("es-CO")}</strong>.
        {markedPercent > 20 ? (
          <span className="text-amber-700 dark:text-amber-500">
            {" "}
            Marcar más de una de cada cinco maniobras describe la conducción normal, no lo brusco.
          </span>
        ) : null}
      </p>
    </div>
  );
}
